import { useMemo } from 'react';
import { TEcowPermit, TPermitWithJobDetails, TSapMo } from '../../../lib/types';
import { GET_BATCHED_MO_DETAILS } from '../../../graph-queries/tar/job';
import { useQuery } from '@apollo/client';
import { GET_PERMITS_BY_PERMIT_NUMBERS } from '../../../graph-queries/tar/permit';
import { FilterFnOption } from '@tanstack/react-table';
import { TPermitFilters } from '../../organisms/PermitFiltersModal/PermitFiltersModal';
import {
  getLabelForPermitStatus,
  getPermitOptions,
} from '../../organisms/JobFiltersModal/JobFiltersModal.util';
import * as ExcelJS from 'exceljs';
import { downloadWorkbook } from '../Certificates/Certificates.util';

export const usePermitsWithDetails = (
  permits: TPermitWithJobDetails[]
): { data: TPermitWithJobDetails[]; loading: boolean } => {
  const moNumbers = useMemo(() => permits.map((p) => p.moNumber).filter(Boolean), [permits]);
  const { data: batchedMosData, loading: mosLoading } = useQuery(GET_BATCHED_MO_DETAILS, {
    variables: { moNumbers, isRealTime: false },
    skip: moNumbers.length === 0,
  });
  const uniqueMos = (batchedMosData?.sapMosByMoNumbers as unknown as TSapMo[]) || [];

  const permitNumbers = useMemo(
    () =>
      permits
        .filter((p) => p?.permitRequirement === 'REQUIRED' && p?.permitNumber)
        .map((p) => p.permitNumber) || [],
    [permits]
  );
  const { data: ecowPermitsData, loading: ecowLoading } = useQuery<{
    permitsByPermitNumbers: TEcowPermit[];
  }>(GET_PERMITS_BY_PERMIT_NUMBERS, {
    variables: { permitNumbers },
    skip: permitNumbers.length === 0,
  });
  const ecowPermits = ecowPermitsData?.permitsByPermitNumbers || [];

  const mergedPermits = useMemo(() => {
    const moMap = new Map<string, TSapMo>(uniqueMos.map((mo) => [mo.workOrder, mo]));
    const ecowPermitMap = new Map<string, string>(
      ecowPermits.map((p) => [p.permitNumber, p.statusComment])
    );

    return permits.map((permit) => {
      const transformedPermit = { ...permit };

      const mo = moMap.get(permit.moNumber);
      if (mo) {
        const labor = mo.labors?.find((l) => l?.taskId === permit.taskId);
        if (labor) {
          transformedPermit.taskDescription = labor.operationDescription;
          transformedPermit.taskWorkCenter = labor.workCenter;
        }
        transformedPermit.viewInFiori = mo.viewInFiori;
      }

      transformedPermit.ecowPermit = {
        ...permit.ecowPermit,
        statusComment: permit.permitNumber
          ? ecowPermitMap.get(permit.permitNumber)
          : permit?.permitRequirement === 'REQUIRED'
            ? 'Required'
            : 'Not Required',
      };

      return transformedPermit;
    });
  }, [permits, batchedMosData, ecowPermitsData]);

  return { data: mergedPermits, loading: mosLoading || ecowLoading };
};

export const permitsGlobalFilterFn: FilterFnOption<TPermitWithJobDetails> = (
  row,
  _columnId,
  { filters, searchString }: { filters: TPermitFilters; searchString: string }
) => {
  const { original } = row;

  if (
    // Search string doesn't match, skip the rows
    searchString &&
    !JSON.stringify(original).toLowerCase().includes(searchString.toLowerCase())
  ) {
    return false;
  }

  //filtering permitStatus
  if (filters['ecowPermit.statusComment']) {
    const permitStatusValue = original.ecowPermit?.statusComment || '';
    const labeledPermitStatus = getLabelForPermitStatus(permitStatusValue);
    const statusValue = getPermitOptions().includes(labeledPermitStatus)
      ? labeledPermitStatus
      : 'Other';
    if (
      // Permit status doesn't match, skip the rows
      filters['ecowPermit.statusComment'].length > 0 &&
      !(statusValue && filters['ecowPermit.statusComment'].includes(statusValue))
    )
      return false;
  }
  //filtering event
  if (filters.event) {
    const eventValue = original.eventDescription || '';
    if (
      // event doesn't match, skip the rows
      filters.event.length > 0 &&
      !(eventValue && filters.event.includes(eventValue))
    )
      return false;
  }
  //filtering task work center
  if (filters.taskWorkCenter) {
    const taskWorkCenterValue = original.taskWorkCenter || '';
    if (
      // task work center doesn't match, skip the rows
      filters.taskWorkCenter.length > 0 &&
      !(taskWorkCenterValue && filters.taskWorkCenter.includes(taskWorkCenterValue))
    )
      return false;
  }

  //filtering permit owner and gocSh1
  if (filters['permitOwner']) {
    const permitOwner = original?.permitOwner;
    if (
      // Permit Fields don't match, skip the rows
      filters['permitOwner'].length > 0 &&
      !(
        permitOwner &&
        filters.permitOwner.some((owner) => permitOwner.toLowerCase().includes(owner.toLowerCase()))
      )
    )
      return false;
  }
  if (filters['gocSh1']) {
    const gocSh1 = original?.gocSh1;
    if (
      // Permit Fields don't match, skip the rows
      filters['gocSh1'].length > 0 &&
      !(gocSh1 && filters.gocSh1.some((goc) => gocSh1.toLowerCase().includes(goc.toLowerCase())))
    )
      return false;
  }

  return true;
};

export const downloadPermitNumbers = async (permitNumbers: string[]) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Unique Permits');
  const fileName = "Unique Filtered Permits in Companion"

  // Add a header row
  worksheet.addRow(['Permit Number']);

  // Add each permit number as a new row
  permitNumbers.forEach((permit) => {
    worksheet.addRow([permit]);
  });

  await downloadWorkbook(workbook, fileName);

}
